// ── Shared onboarding-tour card placement ──────────────────────────────────
// A single, direction-aware algorithm used by both the B2C and Backoffice tours.
//
// Goals:
//  • The card NEVER covers the target element when any side has room.
//  • Works at any viewport size and in both LTR and RTL.
//  • Uses the card's REAL measured size (not a fixed estimate) for accurate fits.
//  • Centers the card on the target and points an arrow at the target's center.

export interface TourRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

export type TourSide = 'above' | 'below' | 'left' | 'right';
export type TourArrowDir = 'up' | 'down' | 'left' | 'right' | null;

export interface TourPlacementOptions {
  /** Target/spotlight rect in viewport coordinates (pass it already padded). */
  anchor: TourRect;
  viewport: { width: number; height: number };
  /** The card's measured size. Width is clamped to the viewport internally. */
  card: { width: number; height: number };
  isRtl: boolean;
  /** Distance between the card and the target. Default 16. */
  gap?: number;
  /** Minimum distance from the viewport edges. Default 8. */
  margin?: number;
  /** Preferred side — tried first when it fits (e.g. 'above' for pax selectors). */
  hint?: TourSide;
  /** Allow left/right placements. Pass false on narrow/mobile screens. Default true. */
  allowHorizontal?: boolean;
}

export interface TourPlacement {
  top: number;
  left: number;
  side: TourSide;
  arrowDir: TourArrowDir;
  /** Arrow offset along the card's horizontal edge (for up/down arrows). */
  arrowX: number;
  /** Arrow offset along the card's vertical edge (for left/right arrows). */
  arrowY: number;
}

const clamp = (v: number, lo: number, hi: number): number =>
  hi < lo ? lo : Math.max(lo, Math.min(v, hi));

/**
 * Decide where to place the tour card relative to a target element.
 *
 * Strategy:
 *  1. Build the four candidate placements (below, above, and — unless horizontal
 *     is disabled — the two sides, ordered by reading direction).
 *  2. A `hint` side, when supplied, is tried first.
 *  3. Pick the first candidate that fully fits (card + gap + margin) on that side.
 *     A fitting side is guaranteed not to overlap the target.
 *  4. If nothing fits (target nearly fills the viewport), fall back to the side
 *     with the most free space and clamp the card on-screen. The arrow is hidden
 *     if this fallback ends up overlapping the target, so it never misleads.
 */
export function computeTourPlacement(opts: TourPlacementOptions): TourPlacement {
  const GAP = opts.gap ?? 16;
  const M = opts.margin ?? 8;
  const allowH = opts.allowHorizontal !== false;
  const { width: vw, height: vh } = opts.viewport;

  // Clamp the card to the available viewport so it is always fully readable.
  const cw = Math.min(opts.card.width, vw - 2 * M);
  const ch = Math.min(opts.card.height, vh - 2 * M);

  const a = opts.anchor;
  const aTop = a.top;
  const aBottom = a.top + a.height;
  const aLeft = a.left;
  const aRight = a.left + a.width;
  const aCenterX = a.left + a.width / 2;
  const aCenterY = a.top + a.height / 2;

  // Cross-axis positions: center the card on the target, clamped into the viewport.
  const hLeft = clamp(aCenterX - cw / 2, M, vw - cw - M); // for below/above
  const vTop = clamp(aCenterY - ch / 2, M, vh - ch - M); // for left/right

  interface Cand {
    side: TourSide;
    top: number;
    left: number;
    arrowDir: TourArrowDir;
    space: number; // free space on that side (gap + margin already subtracted)
    need: number; // card extent required on that side's main axis
  }

  const below: Cand = {
    side: 'below', top: aBottom + GAP, left: hLeft, arrowDir: 'up',
    space: vh - aBottom - GAP - M, need: ch,
  };
  const above: Cand = {
    side: 'above', top: aTop - GAP - ch, left: hLeft, arrowDir: 'down',
    space: aTop - GAP - M, need: ch,
  };
  const right: Cand = {
    side: 'right', top: vTop, left: aRight + GAP, arrowDir: 'left',
    space: vw - aRight - GAP - M, need: cw,
  };
  const left: Cand = {
    side: 'left', top: vTop, left: aLeft - GAP - cw, arrowDir: 'right',
    space: aLeft - GAP - M, need: cw,
  };

  // Reading-direction preference for the horizontal pair: in LTR, prefer the
  // right of the target; in RTL, prefer the left.
  const horiz = allowH ? (opts.isRtl ? [left, right] : [right, left]) : [];
  let order: Cand[] = [below, above, ...horiz];

  if (opts.hint) {
    const map: Record<TourSide, Cand> = { below, above, left, right };
    const hinted = map[opts.hint];
    const hintAllowed =
      hinted && (allowH || opts.hint === 'above' || opts.hint === 'below');
    if (hintAllowed) order = [hinted, ...order.filter((c) => c !== hinted)];
  }

  const fit = order.find((c) => c.space >= c.need);
  const chosen = fit ?? [...order].sort((x, y) => y.space - x.space)[0];

  // Clamp into the viewport on both axes. For a fitting side this is a no-op on
  // the main axis, so the card stays clear of the target. For the fallback it
  // keeps the card readable even if that means overlapping a very large target.
  const cardTop = clamp(chosen.top, M, vh - ch - M);
  const cardLeft = clamp(chosen.left, M, vw - cw - M);

  // Recompute the arrow offset after clamping so it still points at the target.
  const arrowX = clamp(aCenterX - cardLeft, 16, cw - 16);
  const arrowY = clamp(aCenterY - cardTop, 16, ch - 16);

  const overlaps =
    cardLeft < aRight &&
    cardLeft + cw > aLeft &&
    cardTop < aBottom &&
    cardTop + ch > aTop;

  return {
    top: cardTop,
    left: cardLeft,
    side: chosen.side,
    arrowDir: overlaps ? null : chosen.arrowDir,
    arrowX,
    arrowY,
  };
}
